import React, { useState, Children } from 'react';
import { DonationCard } from './DonationCard';
import { HeartIcon, UsersIcon, ClockIcon, TrendingUpIcon, BarChart4Icon, ScanFaceIcon, CheckCircleIcon, ShieldCheckIcon } from 'lucide-react';
export function CharityDonationSection() {
  const [userContribution, setUserContribution] = useState(75);
  const [donationAmount, setDonationAmount] = useState('');
  const [showFaceID, setShowFaceID] = useState(false);
  const [faceIDProgress, setFaceIDProgress] = useState(0);
  const [faceIDSuccess, setFaceIDSuccess] = useState(false);
  const [quickDonationAmount, setQuickDonationAmount] = useState(0);
  const featuredCharity = {
    name: "Royal Children's Hospital",
    description: 'Help us fund a new CT scanner for pediatric diagnostics',
    currentAmount: 128750,
    targetAmount: 250000,
    contributors: 1842,
    estimatedDaysLeft: 45,
    userContribution: userContribution
  };
  const handleQuickDonate = (amount: number) => {
    setQuickDonationAmount(amount);
    setShowFaceID(true);
    setFaceIDProgress(0);
    // Simulate FaceID scanning progress
    const interval = setInterval(() => {
      setFaceIDProgress(prevProgress => {
        const newProgress = prevProgress + 20;
        if (newProgress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setFaceIDSuccess(true);
            // After showing success, add donation and close
            setTimeout(() => {
              setUserContribution(prev => prev + amount);
              setShowFaceID(false);
              setFaceIDSuccess(false);
              setQuickDonationAmount(0);
            }, 1000);
          }, 500);
        }
        return newProgress > 100 ? 100 : newProgress;
      });
    }, 200);
  };
  const handleDonate = () => {
    if (!donationAmount || isNaN(Number(donationAmount))) return;
    const amount = Number(donationAmount);
    setUserContribution(userContribution + amount);
    setDonationAmount('');
  };
  // Calculate percentage complete
  const percentComplete = Math.round(featuredCharity.currentAmount / featuredCharity.targetAmount * 100);
  // Calculate estimated completion date
  const today = new Date();
  const completionDate = new Date(today.setDate(today.getDate() + featuredCharity.estimatedDaysLeft));
  const formattedDate = completionDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  return <div className="bg-card dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700" data-id="element-248">
      {/* Face ID Modal */}
      {showFaceID && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-249">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-sm w-full mx-4" data-id="element-250">
            <div className="flex flex-col items-center text-center" data-id="element-251">
              <div className="relative w-20 h-20 mb-4" data-id="element-252">
                <div className="absolute inset-0 rounded-full bg-primary/10 animate-ping" data-id="element-253"></div>
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-254">
                  <ScanFaceIcon className="w-12 h-12 text-primary" data-id="element-255" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-text-primary dark:text-white mb-1" data-id="element-256">
                {faceIDSuccess ? 'Identity Verified' : 'Verify Your Identity'}
              </h3>
              <p className="text-sm text-text-secondary dark:text-gray-400 mb-6" data-id="element-257">
                {faceIDSuccess ? `Donating £${quickDonationAmount}` : 'Please look at your device to authenticate with Face ID'}
              </p>
              {!faceIDSuccess && <div className="w-full max-w-xs mb-4" data-id="element-258">
                  <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-259">
                    <div className="h-full bg-primary rounded-full transition-all duration-300 ease-out" style={{
                width: `${faceIDProgress}%`
              }} data-id="element-260"></div>
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-text-secondary dark:text-gray-400" data-id="element-261">
                    <span data-id="element-262">Scanning...</span>
                    <span data-id="element-263">{faceIDProgress}%</span>
                  </div>
                </div>}
              {faceIDSuccess && <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-2 animate-bounce" data-id="element-264">
                  <CheckCircleIcon className="w-10 h-10 text-green-600 dark:text-green-400" data-id="element-265" />
                </div>}
              <div className="flex items-center mt-4" data-id="element-266">
                <ShieldCheckIcon className="w-4 h-4 text-primary mr-2" data-id="element-267" />
                <p className="text-xs text-text-secondary dark:text-gray-400" data-id="element-268">
                  Secured by Cauri Face Authentication
                </p>
              </div>
            </div>
          </div>
        </div>}
      <div className="p-4 border-b border-gray-100 dark:border-gray-700" data-id="element-269">
        <h2 className="text-lg font-semibold text-text-primary dark:text-white" data-id="element-270">
          Your Charity Partner
        </h2>
      </div>
      <div className="p-6" data-id="element-271">
        <div className="flex flex-col md:flex-row gap-6" data-id="element-272">
          <div className="md:w-2/3" data-id="element-273">
            <DonationCard currentProgress={featuredCharity.currentAmount} donationTarget={featuredCharity.targetAmount} contributors={featuredCharity.contributors} projectName={featuredCharity.name} projectDescription={featuredCharity.description} data-id="element-274" />
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-275">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-276">
                <div className="flex items-center mb-2" data-id="element-277">
                  <UsersIcon className="w-5 h-5 text-primary mr-2" data-id="element-278" />
                  <h3 className="font-medium text-text-primary dark:text-white" data-id="element-279">
                    Contributors
                  </h3>
                </div>
                <p className="text-2xl font-bold text-text-primary dark:text-white" data-id="element-280">
                  {featuredCharity.contributors}
                </p>
                <p className="text-xs text-text-muted dark:text-gray-400" data-id="element-281">
                  People supporting this cause
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-282">
                <div className="flex items-center mb-2" data-id="element-283">
                  <ClockIcon className="w-5 h-5 text-secondary mr-2" data-id="element-284" />
                  <h3 className="font-medium text-text-primary dark:text-white" data-id="element-285">
                    Estimated Completion
                  </h3>
                </div>
                <p className="text-2xl font-bold text-text-primary dark:text-white" data-id="element-286">
                  {featuredCharity.estimatedDaysLeft} days
                </p>
                <p className="text-xs text-text-muted dark:text-gray-400" data-id="element-287">
                  Target date: {formattedDate}
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg" data-id="element-288">
                <div className="flex items-center mb-2" data-id="element-289">
                  <TrendingUpIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-290" />
                  <h3 className="font-medium text-text-primary dark:text-white" data-id="element-291">
                    Daily Average
                  </h3>
                </div>
                <p className="text-2xl font-bold text-text-primary dark:text-white" data-id="element-292">
                  £
                  {Math.round(featuredCharity.currentAmount / (90 - featuredCharity.estimatedDaysLeft)).toLocaleString()}
                </p>
                <p className="text-xs text-text-muted dark:text-gray-400" data-id="element-293">
                  Average donations per day
                </p>
              </div>
            </div>
          </div>
          <div className="md:w-1/3 bg-gray-50 dark:bg-gray-700 p-5 rounded-xl" data-id="element-294">
            <div className="flex items-center mb-4" data-id="element-295">
              <HeartIcon className="w-5 h-5 text-secondary mr-2" data-id="element-296" />
              <h3 className="font-medium text-text-primary dark:text-white" data-id="element-297">
                Your Contribution
              </h3>
            </div>
            <div className="mb-6" data-id="element-298">
              <p className="text-3xl font-bold text-text-primary dark:text-white" data-id="element-299">
                £{userContribution}
              </p>
              <div className="flex items-center mt-1" data-id="element-300">
                <BarChart4Icon className="w-4 h-4 text-primary mr-1" data-id="element-301" />
                <p className="text-sm text-text-muted dark:text-gray-400" data-id="element-302">
                  {userContribution > 0 ? `You've contributed ${(userContribution / featuredCharity.currentAmount * 100).toFixed(2)}% of the total` : 'Make your first donation today'}
                </p>
              </div>
              <div className="mt-4 space-y-3" data-id="element-303">
                <p className="text-sm font-medium text-text-primary dark:text-white" data-id="element-304">
                  Make a donation
                </p>
                <div className="flex space-x-2" data-id="element-305">
                  <input type="text" value={donationAmount} onChange={e => setDonationAmount(e.target.value)} placeholder="Amount in £" className="w-32 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg text-text-primary dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-306" />
                  <button onClick={handleDonate} className="px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-medium hover:opacity-90 transition-opacity" data-id="element-307">
                    Donate
                  </button>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600" data-id="element-308">
                <p className="text-sm font-medium text-text-primary dark:text-white mb-2" data-id="element-309">
                  Quick amounts
                </p>
                <div className="grid grid-cols-3 gap-2" data-id="element-310">
                  {[5, 10, 25, 50, 100, 200].map(amount => <button key={amount} onClick={() => handleQuickDonate(amount)} className="py-1 px-2 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-text-primary dark:text-white text-sm transition-colors" data-id="element-311">
                      £{amount}
                    </button>)}
                </div>
              </div>
            </div>
            <button className="w-full mt-4 text-primary dark:text-primary-light text-sm font-medium hover:underline" data-id="element-312">
              View donation history
            </button>
          </div>
        </div>
      </div>
    </div>;
}